/**
 * Environment helpers shared across server and client modules. Mirrors the
 * canonical precedence used elsewhere (auth, cron, email): `VERCEL_ENV` first
 * because preview deploys still build with `NODE_ENV=production`; only
 * `VERCEL_ENV=production` is truly prod.
 */

/** A non-production environment (local dev or a Vercel preview). Gates the
 *  `/office/tools` nav item + page. */
export function isNonProdEnv(): boolean {
  const env = process.env.VERCEL_ENV ?? process.env.NODE_ENV;
  return env !== "production";
}

/**
 * Which environments report to Sentry: deployed ones only.
 *
 * Local development was ~99.5% of everything Sentry held (211 events in a
 * week against 1 from production), so a real production error sat invisible
 * among a laptop's noise — and burned the plan's event quota to do it. A dev
 * error is already on your terminal; it doesn't need shipping.
 *
 * `||`, not `??`: a stray empty `VERCEL_ENV=` line in a local .env must read
 * as absent, not as a deployed environment. The browser bundle sees only the
 * NEXT_PUBLIC_ variant, inlined at build time — it exists because the Vercel
 * project's "Automatically expose System Environment Variables" setting is
 * on; turning that off silently disables client-side Sentry.
 */
export function sentryEnabled(): boolean {
  const env =
    process.env.VERCEL_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV || null;
  return env !== null && env !== "development";
}

/** Normalize a host or full URL to a scheme-qualified origin with no trailing
 *  slash (a bare host gets `https://`). */
function toOrigin(value: string): string {
  const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  return withScheme.replace(/\/+$/, "");
}

/**
 * The app's public base URL (scheme + host, no trailing slash) for contexts with
 * NO request — queue workers, crons, and any server code that builds an absolute
 * link (Slack notification deep links, account links in outbound email/WhatsApp).
 * It resolves to the environment the code is RUNNING in, never another:
 *   1. `NEXT_PUBLIC_SITE_URL` — the explicit per-environment override. Set it in
 *      local `.env` to the dev tunnel (`https://brandon.dev.studio.chat`) and in
 *      Vercel production to `https://studio.chat`.
 *   2. Vercel production → the project's production domain
 *      (`VERCEL_PROJECT_PRODUCTION_URL`, e.g. studio.chat).
 *   3. A Vercel preview / branch deploy → that deployment's own URL
 *      (`VERCEL_URL`).
 *   4. Local fallback → `http://localhost:3026` (the dev port).
 * So a notification raised locally links to the local/tunnel host, a preview
 * deploy links to itself, and production links to studio.chat — never the wrong
 * environment. Request-scoped code prefers `requestOrigin()` (the real forwarded
 * host); this is its no-request counterpart.
 */
export function siteUrl(): string {
  const explicit = process.env.NEXT_PUBLIC_SITE_URL?.trim();
  if (explicit) return toOrigin(explicit);
  if (
    process.env.VERCEL_ENV === "production" &&
    process.env.VERCEL_PROJECT_PRODUCTION_URL
  ) {
    return toOrigin(process.env.VERCEL_PROJECT_PRODUCTION_URL);
  }
  if (process.env.VERCEL_URL) return toOrigin(process.env.VERCEL_URL);
  return "http://localhost:3026";
}
